import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { IMFALoginState } from "../../../model/auth/mfa.model";
import { AuthService } from "../../../services/auth/auth.service";
import { JwtService } from "../../../services/auth/jwt.service";
import { SessionService } from "../../../services/session.service";
import { ToastService } from "../../../services/toast.service";
import { UserService } from "../../../services/user/user.service";
import { logger } from "../../../util/Logger";

const className = "MFAVerificationComponent";

@Component({
  selector: "app-mfa-verification",
  templateUrl: "./mfa-verification.component.html",
  styleUrls: ["./mfa-verification.component.scss"],
  standalone: false,
})
export class MFAVerificationComponent implements OnInit, OnDestroy {
  public mfaForm: FormGroup;
  public mfaState: IMFALoginState;
  public isResendingToken = false;
  public resendCooldown = 0;
  private resendInterval: any;

  constructor(
    private session: SessionService,
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private jwtService: JwtService,
  ) {
    // Get MFA state from navigation extras or redirect to login
    const navigation = this.router.getCurrentNavigation();
    this.mfaState = navigation?.extras?.state as IMFALoginState;

    if (!this.mfaState) {
      this.router.navigate(["/login"]);
      return;
    }
  }

  ngOnInit() {
    this.mfaForm = this.fb.group({
      token: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });
    this.sendToken();
  }

  ngOnDestroy() {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }
  }

  onSubmit() {
    const signature = className + ".onSubmit: ";

    if (this.mfaForm.invalid || !this.mfaState.email) {
      return;
    }

    this.session
      .lockInputRx(
        this.authService.verifyMFAToken(
          this.mfaState.email,
          this.mfaForm.value.token,
          this._getUserDeviceType(),
        ),
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            // MFA verification successful, user is already authenticated
            // Navigate to dashboard
            this.jwtService.setJWTMFARequiredString("false");
            this.router.navigateByUrl(this.userService.defaultLandingPage);
          } else {
            this.toastService.showError("Invalid verification code");
            this.mfaForm.get("token")?.setValue("");
          }
        },
        error: (error) => {
          logger.error(signature + `Error verifying MFA token: ${error}`);
          this.toastService.showError("Verification failed. Please try again.");
          this.mfaForm.get("token")?.setValue("");
        },
      });
  }

  sendToken() {
    if (!this.mfaState.email || this.isResendingToken || this.resendCooldown > 0) {
      return;
    }

    this.isResendingToken = true;

    this.authService.sendMFAToken(this.mfaState.email).subscribe({
      next: (response) => {
        if (response.success) {
          this._startResendCooldown();
          this.toastService.showSuccess("Verification code sent successfully");
        } else {
          this.toastService.showError("Failed to send verification code");
        }
        this.isResendingToken = false;
      },
      error: (error) => {
        logger.error(className + ".onResendToken: " + error);
        this.toastService.showError("Failed to send verification code");
        this.isResendingToken = false;
      },
    });
  }

  private _startResendCooldown() {
    this.resendCooldown = 60; // 60 seconds cooldown
    this.resendInterval = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        clearInterval(this.resendInterval);
      }
    }, 1000);
  }

  private _getUserDeviceType(): string {
    const userAgent = navigator.userAgent;
    if (/Mobi|Android/i.test(userAgent)) {
      return "Mobile";
    } else if (/Tablet|iPad/i.test(userAgent)) {
      return "Tablet";
    } else {
      return "Desktop";
    }
  }

  onBackToLogin() {
    this.router.navigate(["/login"]);
  }

  get f() {
    return this.mfaForm.controls;
  }
}
